import { concat, Observable, Observer } from 'rxjs';
import { AppContext } from '../data/app-context';
import {
    EnvironmentModule,
    EnvironmentModuleEntryPoint,
    EnvironmentModuleEntryPointType,
    EnvironmentModuleEntryPointWithToolConditionResult
} from '../manifest/environment-modules';
import { InventoryQueryCaches } from '../shared/inventory-query-caches';
import { Connection } from './connection';
import { ToolConditionValidator, ToolConditionWeight } from './tool-condition-validator';

/**
 * Tool Connection query class.
 * @dynamic
 */
export class ToolConnectionQuery {
    /**
     * Create an observable to determine the collection of tools to be applicable to the connection.
     *
     * @param appContext the application context.
     * @param caches the share inventory query caches.
     * @param connection the connection object.
     * @param solution the entry point object of the solution.
     */
    public static queryToolsList(
        appContext: AppContext,
        caches: InventoryQueryCaches,
        connection: Connection,
        solution: EnvironmentModuleEntryPoint): Observable<EnvironmentModuleEntryPointWithToolConditionResult[]> {
        return ToolConnectionQuery.queryEntryPointList(
            appContext, caches, connection, solution, [EnvironmentModuleEntryPointType.Tool]);
    }

    /**
     * Create an observable to determine the collection of settings to be applicable to the connection.
     *
     * @param appContext the application context.
     * @param caches the share inventory query caches.
     * @param connection the connection object.
     * @param solution the entry point object of the solution.
     */
    public static querySettingsFormsList(
        appContext: AppContext,
        caches: InventoryQueryCaches,
        connection: Connection,
        solution: EnvironmentModuleEntryPoint): Observable<EnvironmentModuleEntryPointWithToolConditionResult[]> {
        return ToolConnectionQuery.queryEntryPointList(
            appContext, caches, connection, solution, [EnvironmentModuleEntryPointType.SettingsForm]);
    }

    /**
     * Create an observable to determine the collection of entry points to be applicable to the connection.
     *
     * @param appContext the application context.
     * @param caches the share inventory query caches.
     * @param connection the connection object.
     * @param solution the entry point object of the solution.
     * @param entryPoints the list of entry points.
     */
    public static validateEntryPoints(
        appContext: AppContext,
        caches: InventoryQueryCaches,
        connection: Connection,
        solution: EnvironmentModuleEntryPoint,
        entryPoints: EnvironmentModuleEntryPoint[]): Observable<EnvironmentModuleEntryPointWithToolConditionResult[]> {
        return ToolConnectionQuery.validateEntryPointList(
            appContext, caches, connection, solution, entryPoints);
    }

    /**
     * gets the list of tools available to the solution and connection
     * @param connection the connection
     * @param solution the solution
     */
    public static getToolsList(connection: Connection, solution: EnvironmentModuleEntryPoint): EnvironmentModuleEntryPoint[] {
        const tools = EnvironmentModule.getEntryPointsByType([EnvironmentModuleEntryPointType.Tool])
            .filter(tool => ToolConnectionQuery.checkToolRequirements(connection, solution, tool));
        // TODO, explore if we should cache this list
        return tools;
    }

    /**
     * get the list of settings forms available to the solution and connection
     * @param connection the connection
     * @param solution the solution
     */
    public static getSettingsFormList(connection: Connection, solution: EnvironmentModuleEntryPoint) {
        const settings = EnvironmentModule.getEntryPointsByType([EnvironmentModuleEntryPointType.SettingsForm])
            .filter(setting => ToolConnectionQuery.checkToolRequirements(connection, solution, setting));
        // TODO, explore if we should cache this list
        return settings;
    }

    /**
     * query for the list of a particular type of entry point
     * @param appContext the app context
     * @param caches the inventory query caches
     * @param connection the connection
     * @param solution the solution entry point.
     * @param entryPointTypes the type list of entry point we want to query.
     */
    private static queryEntryPointList(
        appContext: AppContext,
        caches: InventoryQueryCaches,
        connection: Connection,
        solution: EnvironmentModuleEntryPoint,
        entryPointTypes: EnvironmentModuleEntryPointType[]): Observable<EnvironmentModuleEntryPointWithToolConditionResult[]> {

        const observablesCollection: Observable<EnvironmentModuleEntryPointWithToolConditionResult>[][] = [];
        const tools: EnvironmentModuleEntryPointWithToolConditionResult[] = [];
        const endpoint = !connection ? null : appContext.authorizationManager.getJeaEndpoint(connection.name);
        EnvironmentModule.getEntryPointsByType(entryPointTypes)
            .filter(entryPoint => !endpoint || entryPoint.parentModule.jea)
            .map(tool => {
                const item = ToolConditionValidator.current(appContext, caches)
                    .scanToolCondition(connection, solution, tool);
                if (item.weight === ToolConditionWeight.NonObservable) {
                    tools.push(<EnvironmentModuleEntryPointWithToolConditionResult>item.result);
                } else if (observablesCollection[item.weight]) {
                    observablesCollection[item.weight].push(<Observable<EnvironmentModuleEntryPointWithToolConditionResult>>item.result);
                } else {
                    observablesCollection[item.weight] = [<Observable<EnvironmentModuleEntryPointWithToolConditionResult>>item.result];
                }
            });

        const observables: Observable<EnvironmentModuleEntryPointWithToolConditionResult>[] = [];
        for (let weight = ToolConditionWeight.Property; weight <= ToolConditionWeight.Script; weight++) {
            if (observablesCollection[weight]) {
                observablesCollection[weight].forEach(item => observables.push(item));
            }
        }

        const observable = new Observable((observer: Observer<EnvironmentModuleEntryPointWithToolConditionResult[]>) => {
            // respond tools that have no conditional observable.
            observer.next(tools);
            // process observable sequentially by using concat.
            const subscription = concat(...observables).subscribe({
                next: data => {
                    data.connectionId = connection && connection.id;
                    tools.push(data);
                    observer.next(tools);
                },
                error: error => observer.error(error),
                complete: () => {
                    observer.complete();
                }
            });

            return () => subscription.unsubscribe();
        });

        return observable;
    }

    /**
     * query for the list of a particular type of entry point
     * @param appContext the app context
     * @param caches the inventory query caches
     * @param connection the connection
     * @param solution the solution entry point.
     * @param entryPointTypes the type list of entry point we want to query.
     */
    private static validateEntryPointList(
        appContext: AppContext,
        caches: InventoryQueryCaches,
        connection: Connection,
        solution: EnvironmentModuleEntryPoint,
        entryPoints: EnvironmentModuleEntryPoint[]): Observable<EnvironmentModuleEntryPointWithToolConditionResult[]> {

        const observablesCollection: Observable<EnvironmentModuleEntryPointWithToolConditionResult>[][] = [];
        const tools: EnvironmentModuleEntryPointWithToolConditionResult[] = [];
        entryPoints
            .map(tool => {
                const item = ToolConditionValidator.current(appContext, caches).scanToolCondition(connection, solution, tool);
                if (item.weight === ToolConditionWeight.NonObservable) {
                    tools.push(<EnvironmentModuleEntryPointWithToolConditionResult>item.result);
                } else if (observablesCollection[item.weight]) {
                    observablesCollection[item.weight].push(<Observable<EnvironmentModuleEntryPointWithToolConditionResult>>item.result);
                } else {
                    observablesCollection[item.weight] = [<Observable<EnvironmentModuleEntryPointWithToolConditionResult>>item.result];
                }
            });

        const observables: Observable<EnvironmentModuleEntryPointWithToolConditionResult>[] = [];
        for (let weight = ToolConditionWeight.Property; weight <= ToolConditionWeight.Script; weight++) {
            if (observablesCollection[weight]) {
                observablesCollection[weight].forEach(item => observables.push(item));
            }
        }

        const observable = new Observable((observer: Observer<EnvironmentModuleEntryPointWithToolConditionResult[]>) => {
            if (observables.length === 0) {
                observer.next(tools);
                observer.complete();
                return;
            }

            // process observable sequentially by using concat, and aggregate to the single tools[] array.
            const subscription = concat(...observables).subscribe({
                next: data => {
                    data.connectionId = connection.id;
                    tools.push(data);
                },
                error: error => observer.error(error),
                complete: () => {
                    observer.next(tools);
                    observer.complete();
                }
            });
            return () => subscription.unsubscribe();
        });

        return observable;
    }

    private static checkToolRequirements(
        connection: Connection, solution: EnvironmentModuleEntryPoint, tool: EnvironmentModuleEntryPoint): boolean {
        if (!tool.requirements) {
            // tool is missing requirements, never show.
            return false;
        }

        const solutionId = EnvironmentModule.createFormattedEntrypoint(solution);

        // process each tool requirement.
        for (let i = 0; i < tool.requirements.length; i++) {
            const req = tool.requirements[i];
            if (req.solutionIds
                && ((!connection && !req.connectionTypes) ||
                    (connection && req.connectionTypes
                        && req.solutionIds.some(s => s === solutionId)
                        && req.connectionTypes.some(c => c === connection.type)
                    )
                )) {
                return true;
            }
        }
        return false;
    }
}
